import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CreditCard, CheckCircle, XCircle, Clock } from 'lucide-react';

import { API_URL } from '../../utils/api';

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      // Payments waiting to be settled are fetched on their own too, so an old one is never cut
      // off by the list limit (they block that customer's payments until settled).
      const fetchAll = async (filter) => {
        const rows = [];
        for (let skip = 0; ; skip += 1000) {
          const page = await axios.get(`${API_URL}/admin/transactions`, { params: { ...filter, skip, limit: 1000 } });
          rows.push(...page.data);
          if (page.data.length < 1000) return rows;
        }
      };
      const [response, unresolved, unfinished] = await Promise.all([
        axios.get(`${API_URL}/admin/transactions`),
        fetchAll({ status: 'unresolved' }),
        fetchAll({ settle_pending: true }),
      ]);
      const waiting = [...unresolved, ...unfinished];
      const waitingIds = new Set(waiting.map(t => t.id));
      setTransactions([...waiting, ...response.data.filter(t => !waitingIds.has(t.id))]);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // A charge with no answer from Sola blocks new phone/SMS payments until it is settled here,
  // after checking the Sola portal (invoice = invitation id, or "sms-..." for an SMS deposit).
  const resolveTransaction = async (transaction, charged) => {
    const question = charged
      ? 'Sola shows this charge went through. Mark it as charged?'
      : 'Sola shows no charge for this. Mark it as not charged (the customer can pay again)?';
    if (!window.confirm(question)) return;
    try {
      const response = await axios.post(`${API_URL}/admin/transactions/${encodeURIComponent(transaction.id)}/resolve`, { charged });
      if (response.data?.note) window.alert(response.data.note);
      await fetchTransactions();
    } catch (error) {
      window.alert(error.response?.data?.detail || 'Could not update this transaction.');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  // Calculate totals
  const totalRevenue = transactions
    .filter(t => t.payment_status === 'paid')
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  
  const successfulTransactions = transactions.filter(t => t.payment_status === 'paid').length;
  const pendingTransactions = transactions.filter(t => t.payment_status === 'pending').length;

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Transactions</h1>
          <p className="text-ink/60 mb-8">View all payment transactions</p>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-ink/60">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">${totalRevenue.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-ink/60">Successful</p>
                  <p className="text-2xl font-bold text-blue-600">{successfulTransactions}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-ink/60">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{pendingTransactions}</p>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-20 text-ink/60">No transactions yet</div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>Session ID</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-mono text-sm">{transaction.id.slice(0, 8)}...</TableCell>
                      <TableCell className="font-mono text-sm text-ink/60">{transaction.session_id?.slice(0, 20)}...</TableCell>
                      <TableCell className="font-medium">${transaction.amount?.toFixed(2)} {transaction.currency?.toUpperCase()}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(transaction.status)}`}>
                          {getStatusIcon(transaction.status)}
                          {transaction.status}
                        </span>
                        {(transaction.status === 'unresolved' || transaction.settle_pending) && (
                          <div className="mt-1 text-xs text-ink/60">
                            Sola invoice: <span className="font-mono">{transaction.invoice || transaction.invitation_id || '—'}</span>
                            {transaction.phone && <> · {transaction.phone}</>}
                            {transaction.ref_num && <> · ref {transaction.ref_num}</>}
                          </div>
                        )}
                        {transaction.settle_pending && (
                          <div className="mt-1">
                            <button type="button" className="text-xs underline" onClick={() => resolveTransaction(transaction, transaction.status === 'completed')}>
                              Finish settling
                            </button>
                          </div>
                        )}
                        {transaction.status === 'unresolved' && (
                          <div className="mt-1 flex gap-1">
                            <button type="button" className="text-xs underline text-green-700" onClick={() => resolveTransaction(transaction, true)}>
                              Was charged
                            </button>
                            <button type="button" className="text-xs underline text-red-700" onClick={() => resolveTransaction(transaction, false)}>
                              Not charged
                            </button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(transaction.payment_status)}`}>
                          {getStatusIcon(transaction.payment_status)}
                          {transaction.payment_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-ink/60">{new Date(transaction.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
